<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('Topic areas') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/admin/settings">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill relative pt-3 h-0">
            <div class="w-full h-full bg-white shadow sm:rounded border border-ultralight overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="flex items-stretch flex-1 min-h-0">
                    <div class="flex flex-col flex-auto min-w-0 overflow-hidden">
                        <div class="xl:hidden border-b border-ultralight px-4 py-4 space-y-4 bg-white">
                            <div class="rounded-lg border border-bordergray-100 bg-stage-background px-5 py-5">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Topic areas') }}</h3>
                                <p class="mt-2 text-sm leading-6 text-gray-500">
                                    {{ $t('Topic areas are simple ticket categories. Internal-only topic areas are visible only to admins and internal employees.') }}
                                </p>
                            </div>

                            <div class="rounded-lg border border-bordergray-100 bg-white px-5 py-5">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Visibility') }}</div>
                                <p class="mt-3 text-sm leading-6 text-gray-500">
                                    {{ $t('Visible only to admins and internal employees') }}
                                </p>
                            </div>

                            <div class="rounded-lg border border-bordergray-100 bg-stage-background px-5 py-5">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Notes') }}</div>
                                <p class="mt-3 text-sm leading-6 text-gray-500">
                                    {{ $t('The topic area cannot be deleted while tickets are still assigned to it.') }}
                                </p>
                            </div>
                        </div>

                        <div class="flex-1 min-h-0 overflow-auto">
                            <div v-if="ticketTopicList.length === 0 && !loading" class="flex h-full items-center justify-center px-6 py-10">
                                <empty-state />
                            </div>

                            <template v-else>
                                <div class="hidden lg:block overflow-hidden">
                                    <div class="align-middle inline-block min-w-full">
                                        <table class="table-listview table-listview--zebra">
                                            <thead>
                                                <tr>
                                                    <th class="table-listview-l-th">{{ $t('Name') }}</th>
                                                    <th class="table-listview-l-th w-80">{{ $t('Visibility') }}</th>
                                                    <th class="table-listview-l-th w-28">{{ $t('Tickets') }}</th>
                                                    <th class="table-listview-l-th table-listview-l-th-right w-52">{{ $t('Action') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white">
                                                <tr>
                                                    <td class="table-listview-l-td align-middle">
                                                        <input
                                                            id="new_ticket_topic_name"
                                                            v-model="newTicketTopic.name"
                                                            :placeholder="$t('Topic area')"
                                                            class="form-input block w-full text-sm"
                                                            type="text"
                                                        >
                                                    </td>
                                                    <td class="table-listview-l-td align-middle">
                                                        <label class="inline-flex items-center">
                                                            <input v-model="newTicketTopic.internal_only" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                                            <span class="ml-2 text-sm text-gray-700">{{ $t('Visible only to admins and internal employees') }}</span>
                                                        </label>
                                                    </td>
                                                    <td class="table-listview-l-td align-middle text-sm text-gray-400">
                                                        -
                                                    </td>
                                                    <td class="table-listview-l-td align-middle text-right">
                                                        <button class="btn-stage-primary" type="button" @click="createTicketTopic">
                                                            {{ $t('Create topic area') }}
                                                        </button>
                                                    </td>
                                                </tr>

                                                <tr v-for="ticketTopic in ticketTopicList" :key="ticketTopic.id">
                                                    <td class="table-listview-l-td align-middle">
                                                        <div>
                                                            <input
                                                                v-model="ticketTopic.name"
                                                                class="form-input block w-full text-sm"
                                                                type="text"
                                                            >
                                                            <div v-if="ticketTopic.tickets_count > 0" class="mt-2 text-xs text-gray-500">
                                                                {{ $t('The topic area cannot be deleted while tickets are still assigned to it.') }}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td class="table-listview-l-td align-middle">
                                                        <label class="inline-flex items-center">
                                                            <input v-model="ticketTopic.internal_only" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                                            <span class="ml-2 text-sm text-gray-700">{{ $t('Visible only to admins and internal employees') }}</span>
                                                        </label>
                                                    </td>
                                                    <td class="table-listview-l-td align-middle text-sm text-gray-500">
                                                        {{ ticketTopic.tickets_count }}
                                                    </td>
                                                    <td class="table-listview-l-td align-middle">
                                                        <div class="flex items-center justify-end gap-3">
                                                            <button class="btn-stage-secondary" type="button" @click="updateTicketTopic(ticketTopic)">
                                                                {{ $t('Save') }}
                                                            </button>
                                                            <button
                                                                :disabled="ticketTopic.tickets_count > 0"
                                                                :class="ticketTopic.tickets_count > 0 ? 'btn btn-white shadow-sm rounded opacity-50 cursor-not-allowed' : 'btn btn-white shadow-sm rounded'"
                                                                type="button"
                                                                @click="openDeleteTicketTopicModal(ticketTopic)"
                                                            >
                                                                {{ $t('Delete') }}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div class="lg:hidden space-y-4 px-4 py-4">
                                    <div class="rounded-lg border border-bordergray-100 bg-white shadow-sm">
                                        <div class="border-b border-ultralight px-4 py-3">
                                            <div class="text-sm font-semibold text-gray-800">{{ $t('Create topic area') }}</div>
                                        </div>
                                        <div class="grid grid-cols-1 gap-4 px-4 py-4">
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="new_ticket_topic_name_mobile">{{ $t('Name') }}</label>
                                                <input
                                                    id="new_ticket_topic_name_mobile"
                                                    v-model="newTicketTopic.name"
                                                    :placeholder="$t('Topic area')"
                                                    class="form-input mt-1 block w-full text-sm"
                                                    type="text"
                                                >
                                            </div>
                                            <div>
                                                <label class="inline-flex items-center">
                                                    <input v-model="newTicketTopic.internal_only" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                                    <span class="ml-2 text-sm text-gray-700">{{ $t('Visible only to admins and internal employees') }}</span>
                                                </label>
                                            </div>
                                            <div class="flex justify-end">
                                                <button class="btn-stage-primary" type="button" @click="createTicketTopic">
                                                    {{ $t('Create topic area') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-for="ticketTopic in ticketTopicList"
                                        :key="'mobile-' + ticketTopic.id"
                                        class="rounded-lg border border-bordergray-100 bg-white shadow-sm"
                                    >
                                        <div class="border-b border-ultralight px-4 py-3">
                                            <div class="flex items-center justify-between gap-3">
                                                <div>
                                                    <div class="text-sm font-semibold text-gray-800">
                                                        {{ ticketTopic.name || $t('Topic area') }}
                                                    </div>
                                                    <div class="mt-1 text-xs text-gray-500">
                                                        {{ ticketTopic.tickets_count }} {{ $t('Tickets') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="grid grid-cols-1 gap-4 px-4 py-4">
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('Name') }}</label>
                                                <input
                                                    v-model="ticketTopic.name"
                                                    class="form-input mt-1 block w-full text-sm"
                                                    type="text"
                                                >
                                            </div>

                                            <div>
                                                <label class="inline-flex items-center">
                                                    <input v-model="ticketTopic.internal_only" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                                    <span class="ml-2 text-sm text-gray-700">{{ $t('Visible only to admins and internal employees') }}</span>
                                                </label>
                                            </div>

                                            <div v-if="ticketTopic.tickets_count > 0" class="text-xs text-gray-500">
                                                {{ $t('The topic area cannot be deleted while tickets are still assigned to it.') }}
                                            </div>

                                            <div class="flex items-center justify-end gap-3">
                                                <button class="btn-stage-secondary" type="button" @click="updateTicketTopic(ticketTopic)">
                                                    {{ $t('Save') }}
                                                </button>
                                                <button
                                                    :disabled="ticketTopic.tickets_count > 0"
                                                    :class="ticketTopic.tickets_count > 0 ? 'btn btn-white shadow-sm rounded opacity-50 cursor-not-allowed' : 'btn btn-white shadow-sm rounded'"
                                                    type="button"
                                                    @click="openDeleteTicketTopicModal(ticketTopic)"
                                                >
                                                    {{ $t('Delete') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>

                    <aside
                        :style="{flex: '0 0 352px', width: '352px'}"
                        class="hidden xl:block border-l border-ultralight"
                    >
                        <div class="h-full overflow-y-auto bg-white">
                            <div class="px-6 py-6 border-b border-ultralight">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Topic areas') }}</h3>
                                <p class="mt-2 text-sm leading-6 text-gray-500">
                                    {{ $t('Topic areas are simple ticket categories. Internal-only topic areas are visible only to admins and internal employees.') }}
                                </p>
                            </div>

                            <div class="px-6 py-6 border-b border-ultralight">
                                <div class="text-xs tracking-wide text-gray-500 mb-3">{{ $t('Visibility') }}</div>
                                <div class="text-sm text-gray-700">
                                    {{ $t('Visible only to admins and internal employees') }}
                                </div>
                                <div class="mt-3 text-xs leading-5 text-gray-500">
                                    {{ $t('External users can no longer select internal-only topic areas when creating tickets.') }}
                                </div>
                            </div>

                            <div class="px-6 py-6 border-b border-ultralight">
                                <div class="text-xs tracking-wide text-gray-500 mb-3">{{ $t('Overview') }}</div>
                                <div class="space-y-2 text-sm text-gray-700">
                                    <div class="flex items-center justify-between gap-4">
                                        <span>{{ $t('Topic areas') }}</span>
                                        <span class="font-semibold">{{ ticketTopicList.length }}</span>
                                    </div>
                                    <div class="flex items-center justify-between gap-4">
                                        <span>{{ $t('Visible only to admins and internal employees') }}</span>
                                        <span class="font-semibold">{{ internalOnlyCount }}</span>
                                    </div>
                                    <div class="flex items-center justify-between gap-4">
                                        <span>{{ $t('Tickets') }}</span>
                                        <span class="font-semibold">{{ assignedTicketsCount }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="px-6 py-6">
                                <div class="text-xs tracking-wide text-gray-500 mb-3">{{ $t('Notes') }}</div>
                                <div class="text-xs leading-5 text-gray-500">
                                    {{ $t('The topic area cannot be deleted while tickets are still assigned to it.') }}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>

                <div class="stage-footer-surface px-4 py-3 sm:px-6 flex items-center justify-between border-t border-ultralight">
                    <div class="text-xs text-gray-500">
                        {{ ticketTopicList.length }} {{ $t('Topic areas') }}
                    </div>
                    <span class="inline-flex items-center">
                        <button class="btn-stage-primary" type="button" @click="createTicketTopic">
                            {{ $t('Create topic area') }}
                        </button>
                    </span>
                </div>
            </div>
        </div>

        <confirm-dialog
            :open="deleteTicketTopicModal"
            :title="$t('Delete topic area').toString()"
            :text="`${$t('Are you sure you want to delete the topic area?')} ${$t('The topic area and all associated data will be permanently removed.')} ${$t('This action cannot be undone')}.`"
            :confirm-label="$t('Delete topic area').toString()"
            confirm-variant="danger"
            @close="closeDeleteTicketTopicModal"
            @confirm="deleteTicketTopic"
        />
    </div>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';

export default {
    components: {ConfirmDialog},
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Topic areas')
        }
    },
    data() {
        return {
            loading: true,
            ticketTopicList: [],
            deleteTicketTopicModal: false,
            ticketTopicToDelete: null,
            newTicketTopic: {
                name: '',
                internal_only: false,
            },
        }
    },
    computed: {
        internalOnlyCount() {
            return this.ticketTopicList.filter((item) => !!item.internal_only).length;
        },
        assignedTicketsCount() {
            return this.ticketTopicList.reduce((sum, item) => sum + Number(item.tickets_count || 0), 0);
        },
    },
    mounted() {
        this.getTicketTopics();
    },
    methods: {
        normalizeCollection(collection) {
            if (Array.isArray(collection)) {
                return collection;
            }

            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }

            return [];
        },
        getTicketTopics() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/ticket-topics').then(function (response) {
                self.ticketTopicList = self.normalizeCollection(response.data);
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        createTicketTopic() {
            const self = this;
            const name = String(self.newTicketTopic.name || '').trim();
            if (name === '') {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('The :attribute field is required', {attribute: self.$i18n.t('name')}).toString(),
                    type: 'error'
                });
                return;
            }

            axios.post('api/workspace/admin/ticket-topics', {
                name: name,
                internal_only: self.newTicketTopic.internal_only,
            }).then(function (response) {
                self.ticketTopicList.unshift(response.data.ticket_topic);
                self.ticketTopicList = self.ticketTopicList.slice().sort(function (a, b) {
                    return String(a.name || '').localeCompare(String(b.name || ''));
                });
                self.newTicketTopic = {
                    name: '',
                    internal_only: false,
                };
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        updateTicketTopic(ticketTopic) {
            const self = this;
            const name = String(ticketTopic.name || '').trim();
            if (name === '') {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('The :attribute field is required', {attribute: self.$i18n.t('name')}).toString(),
                    type: 'error'
                });
                self.getTicketTopics();
                return;
            }

            axios.put('api/workspace/admin/ticket-topics/' + ticketTopic.id, {
                name: name,
                internal_only: ticketTopic.internal_only,
            }).then(function (response) {
                const index = self.ticketTopicList.findIndex(function (item) {
                    return Number(item.id) === Number(ticketTopic.id);
                });

                if (index >= 0) {
                    self.$set(self.ticketTopicList, index, response.data.ticket_topic);
                }

                self.ticketTopicList = self.ticketTopicList.slice().sort(function (a, b) {
                    return String(a.name || '').localeCompare(String(b.name || ''));
                });
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
                self.getTicketTopics();
            });
        },
        openDeleteTicketTopicModal(ticketTopic) {
            if (!ticketTopic || Number(ticketTopic.tickets_count) > 0) {
                return;
            }

            this.ticketTopicToDelete = ticketTopic;
            this.deleteTicketTopicModal = true;
        },
        closeDeleteTicketTopicModal() {
            this.deleteTicketTopicModal = false;
            this.ticketTopicToDelete = null;
        },
        deleteTicketTopic() {
            const self = this;
            const ticketTopic = self.ticketTopicToDelete;
            if (!ticketTopic || Number(ticketTopic.tickets_count) > 0) {
                self.closeDeleteTicketTopicModal();
                return;
            }

            axios.delete('api/workspace/admin/ticket-topics/' + ticketTopic.id).then(function () {
                self.ticketTopicList = self.ticketTopicList.filter(function (item) {
                    return Number(item.id) !== Number(ticketTopic.id);
                });
                self.closeDeleteTicketTopicModal();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.closeDeleteTicketTopicModal();
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while deleting data').toString(),
                    type: 'error'
                });
                self.getTicketTopics();
            });
        }
    }
}
</script>
